import React, { useMemo, useState } from 'react';

import type { SancaiLiugeResult, StageLevel } from '../lib/sancai-liuge';
import type { ProfessionalNameStudyReport } from '../lib/professional-name-study';
import { PROFESSIONAL_NAME_STUDY_DEMO_REPORT, WUXING_COLOR_MAP, toDisplayLuckTag } from '../lib/professional-name-study';
import XuanYuanReportFooter from './XuanYuanReportFooter';
import XuanYuanTopBar from './XuanYuanTopBar';

const KAITI_FONT = "font-['Kaiti','STKaiti','KaiTi_GB2312','楷体','serif']";
const PRO_TEACHER_WECHAT = 'dao6257';
const PRO_TEACHER_QR_SRC = '/contact/ruoxuan-wechat-qr.png';
const MOBILE_WECHAT_URL = 'weixin://';

const stageStyle = (level: StageLevel): string => {
  if (level === '大吉') return 'bg-emerald-100 text-emerald-800 border-emerald-300';
  if (level === '吉') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (level === '平') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (level === '小凶') return 'bg-orange-50 text-orange-700 border-orange-200';
  if (level === '凶') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-600 border-slate-300';
};

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <div className="relative my-5">
    <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-[#D8C1AA] to-transparent" />
    <div className="relative mx-auto w-fit rounded-full border border-[#DCC7B2] bg-gradient-to-r from-[#FDF9F2] via-[#FFFDF9] to-[#FDF9F2] px-5 py-2 shadow-[0_4px_14px_rgba(148,87,67,0.08)] sm:px-7">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#C87635]">✦</span>
        <h2 className={`text-[18px] font-bold tracking-[0.16em] text-[#4A3C31] ${KAITI_FONT}`}>{title}</h2>
        <span className="text-xs text-[#C87635]">✦</span>
      </div>
    </div>
  </div>
);

const getLuckBadgeClass = (tag: string): string => {
  const display = toDisplayLuckTag(tag);
  if (display === '半吉' || display === '半凶') return 'bg-[#D08A45] text-white';
  if (display.includes('吉') && !display.includes('凶')) return 'bg-[#528A5B] text-white';
  if (display.includes('凶') && !display.includes('吉')) return 'bg-[#C24B4B] text-white';
  return 'bg-[#8E6E56] text-white';
};

const getAnalysisTagClass = (label: string): string => {
  if (label === '基业') return 'bg-[#B06A6A]';
  if (label === '家庭') return 'bg-[#507C8C]';
  if (label === '健康') return 'bg-[#7E9A68]';
  if (label === '解读') return 'bg-[#A06F8A]';
  return 'bg-[#8E6E56]';
};

interface SancaiLiugeProReportPageProps {
  result: SancaiLiugeResult;
  reportData: ProfessionalNameStudyReport | null;
  reportError?: string;
  currentAge?: number | null;
  etaLabel?: string;
  isDemo?: boolean;
  reportQrDataUrl?: string;
  nameFrameNode: React.ReactNode;
  showRenameConsult: boolean;
  onBack: () => void;
  onOpenProExport: () => void;
  onRetry: () => void;
  onToggleRenameConsult: () => void;
}

const SancaiLiugeProReportPage: React.FC<SancaiLiugeProReportPageProps> = ({
  result,
  reportData,
  reportError = '',
  currentAge = null,
  etaLabel = '18-28秒',
  isDemo = false,
  reportQrDataUrl = '',
  nameFrameNode,
  showRenameConsult,
  onBack,
  onOpenProExport,
  onRetry,
  onToggleRenameConsult,
}) => {
  const ageText = Number.isFinite(Number(currentAge)) && Number(currentAge) > 0 ? `${Number(currentAge)}岁` : '未填写';
  const [wechatNotice, setWechatNotice] = useState('');
  const isMobile = useMemo(() => /Android|iPhone|iPad|iPod|Mobile/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : ''), []);

  const handleCopyWechat = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(PRO_TEACHER_WECHAT);
      } else {
        const temp = document.createElement('input');
        temp.value = PRO_TEACHER_WECHAT;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setWechatNotice('已复制成功');
      window.setTimeout(() => setWechatNotice(''), 1800);
      if (isMobile) {
        window.setTimeout(() => {
          window.location.href = MOBILE_WECHAT_URL;
        }, 120);
      }
    } catch {
      setWechatNotice(`复制失败，请手动添加 ${PRO_TEACHER_WECHAT}`);
    }
  };

  if (isDemo) {
    const demoData = PROFESSIONAL_NAME_STUDY_DEMO_REPORT;
    const demoAgeText = '32岁';
    const demoScoreMatch = demoData.finalAdvice.join(' ').match(/(\d+(?:\/100)?)\s*分/u);
    const demoScore = demoScoreMatch ? `${demoScoreMatch[1]}分` : '45/100分';
    const demoSummaryTag = '起势不差后程需养';
    const demoGridSubtitleMap: Record<string, string> = {
      tiange: '意志薄弱的家庭寂寞数。',
      renge: '意志薄弱的家庭寂寞数。',
      dige: '',
      waige: '',
      biange: '',
      zongge: '',
    };
    const stageGridMap: Record<string, { grid: string; relation: string }> = {
      xiantian: { grid: '人格14', relation: '' },
      houtian: { grid: '变格13', relation: '后天努力的结果。此13数“春日牡丹”为吉，意味着命主天资聪颖，富学艺才能，具备智谋奇略。通过自身的勤奋和智慧，能够克服重重难关，取得显著的成就，获得名利双收。您是一个善于学习和思考的人，总能凭借自己的聪明才智，在困境中找到突破口，甚至在别人看来棘手的问题，您也能巧妙化解。' },
      shaonian: { grid: '地格11', relation: '' },
      qingnian: { grid: '人格14 × 地格11', relation: '' },
      zhuangnian: { grid: '天格12 × 人格14', relation: '' },
      zhongnian: { grid: '人格14 × 外格9', relation: '' },
      laonian: { grid: '总格22', relation: '' },
      jiating: { grid: '地格11 × 外格9', relation: '' },
    };

    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,#efe4d1_0%,#f8f4ec_22%,#fbfaf7_100%)]">
        <XuanYuanTopBar
          title="轩辕国学·AI 专业解析"
          onBack={onBack}
          variant="light"
          maxWidthClassName="max-w-5xl"
        />
        <div className="mx-auto max-w-5xl px-4 py-6 pb-24">
          <div className="overflow-hidden rounded-[30px] border border-[#e8d8be] bg-[linear-gradient(180deg,rgba(255,251,244,0.98),rgba(248,241,231,0.98))] shadow-[0_24px_60px_rgba(110,76,35,0.12)]">
            <div className="relative overflow-hidden border-b border-[#ead8bd] px-6 py-10 sm:px-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,221,151,0.4),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(175,120,56,0.16),transparent_30%)]" />
              <div className="relative">
                <div className="absolute right-0 top-0 inline-flex items-center rounded-full border border-[#e3c99f] bg-white/85 px-3 py-1 text-xs font-semibold tracking-[0.22em] text-[#8f5a1b]">
                  SAMPLE
                </div>
                <h1 className={`mt-6 text-[30px] font-bold tracking-[0.08em] text-[#4d3424] sm:text-[38px] ${KAITI_FONT}`}>张一二姓名学报告</h1>
                <p className="mt-2 text-sm leading-6 text-[#856957] font-sans">
                  当前为专业版姓名学报告样例页，展示固定案例内容。
                </p>
              </div>
            </div>
            
            <div className="px-6 py-8 sm:px-10">
              {/* 姓名档案 */}
              <SectionTitle title="姓名档案" />
              <div className="rounded-[24px] border border-[#eadac3] bg-white/75 px-5 py-5 shadow-[0_10px_24px_rgba(131,91,51,0.06)]">
                <div className="rounded-[22px] border border-[#ead7c0] bg-[linear-gradient(135deg,rgba(255,250,242,0.98),rgba(248,239,225,0.92))] p-5 shadow-[0_12px_26px_rgba(131,91,51,0.08)]">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="flex flex-wrap items-end gap-3">
                        <p className={`text-[30px] font-bold tracking-[0.22em] text-[#4a3527] ${KAITI_FONT}`}>张一二</p>
                        <span className="inline-flex items-center rounded-full bg-[#4A3C31] px-3 py-1 text-sm font-semibold text-white shadow-[0_8px_18px_rgba(74,60,49,0.16)]">
                          {demoScore}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-sm text-[#7b6758] font-sans">
                        <span>性别：男</span>
                        <span>年龄：{demoAgeText}</span>
                        <span>三才：木-火-木</span>
                      </div>
                    </div>
                    <div className="rounded-full border border-[#d8bf9d] bg-white/90 px-4 py-2 text-sm font-semibold tracking-[0.18em] text-[#8f5a1b] shadow-[0_8px_16px_rgba(143,90,27,0.08)]">
                      {demoSummaryTag}
                    </div>
                  </div>
                  <p className="mt-4 text-[14px] leading-7 text-[#6d5446] font-sans">
                    {demoData.currentFocus}
                  </p>
                </div>
                <div className="mt-4 space-y-2 text-[15px] leading-7 text-[#5f4738] font-sans">
                  {demoData.overview.map((item, index) => (
                    <p key={`overview-${index}`}>{item}</p>
                  ))}
                  <p>{demoData.currentFocus}</p>
                </div>
              </div>

              {/* 六格数理基础计算 */}
              <SectionTitle title="六格数理基础计算" />
              <div className="rounded-[24px] border border-[#eadac3] bg-white/75 px-3 py-4 shadow-[0_10px_24px_rgba(131,91,51,0.06)] sm:px-5">
                <div className="p-1">
                  {nameFrameNode}
                </div>
              </div>

              {/* 六格数理深度解析 */}
              <SectionTitle title="六格数理深度解析" />
              <div className="space-y-4">
                {demoData.gridAnalyses.map((grid) => (
                  <div key={grid.key} className="rounded-[24px] border border-[#e8d8c1] bg-white/85 p-4 shadow-[0_10px_26px_rgba(122,86,45,0.06)] sm:p-5">
                    <div className="flex w-full flex-row overflow-hidden rounded-xl border border-[#E8DFD1] bg-white shadow-[0_4px_14px_rgba(148,87,67,0.08)]">
                      <div className="relative flex w-10 shrink-0 items-center justify-center bg-[#945743] py-2.5 text-white sm:w-12 sm:py-3">
                        <span className={`text-base font-bold leading-none tracking-[0.12em] sm:text-lg ${KAITI_FONT}`} style={{ writingMode: 'vertical-rl', textOrientation: 'upright' }}>
                          {grid.label}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2.5 bg-[#FDFBF7] px-3 py-2.5 sm:px-4 sm:py-3">
                          <div className="pr-1 text-[13px] font-bold leading-[1.45] tracking-[0.01em] text-[#4A3C31] font-sans sm:text-[14px]">
                            <div>{`【${grid.label}：${grid.number}】${grid.title}${demoGridSubtitleMap[grid.key] ? `：${demoGridSubtitleMap[grid.key]}` : ''}`}</div>
                            <div className="mt-1 text-[12px] text-[#8A7C6E]">({grid.genderLuck})</div>
                          </div>
                          <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white shadow-sm sm:h-10 sm:w-10 sm:text-[13px] ${getLuckBadgeClass(grid.genderLuck)} ${KAITI_FONT}`}>
                            {grid.genderLuck.includes('吉') && !grid.genderLuck.includes('凶') ? '吉' : grid.genderLuck.includes('凶') && !grid.genderLuck.includes('吉') ? '凶' : '平'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2 px-1">
                      {[
                        { label: '基业', value: grid.base },
                        { label: '家庭', value: grid.family },
                        { label: '健康', value: grid.health },
                        { label: '整体', value: grid.overall?.replace(/^数理含义[：:]/, '') || '' },
                      ].filter((item) => item.value).map((item) => (
                        <div key={`${grid.key}-${item.label}`} className="flex items-start text-[14px]">
                          <span className={`mr-2.5 inline-flex min-h-[22px] items-center justify-center rounded-sm px-2 py-0.5 text-[11px] font-bold leading-none tracking-[0.12em] text-white ${getAnalysisTagClass(item.label)} ${KAITI_FONT}`}>
                            {item.label}
                          </span>
                          <span className="flex-1 leading-6 text-[#5A4535] font-sans">{item.value}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 text-[15px] leading-7 text-[#5d4738] font-sans">
                      <div className="flex items-start gap-2">
                        <span className={`mt-0.5 inline-flex min-h-[22px] shrink-0 items-center justify-center rounded-sm px-2 py-0.5 text-[11px] font-bold leading-none tracking-[0.12em] text-white ${getAnalysisTagClass('解读')} ${KAITI_FONT}`}>
                          解读
                        </span>
                        <span className="flex-1">{grid.meaning.replace(/^现代解读[：:]/, '')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 六运联动 */}
              <SectionTitle title="六运联动" />
              <div className="mt-4 space-y-3">
                {demoData.stageAnalyses.map((stage) => {
                  const stageMeta = stageGridMap[stage.key] || { grid: '格局联动', relation: '需结合六格与阶段共同观察。' };
                  return (
                    <div key={stage.key} className="rounded-[20px] border border-[#ecdfcc] bg-[#fffdfa] px-5 py-4 shadow-[0_8px_18px_rgba(122,86,45,0.05)]">
                      <div className="flex items-center justify-between gap-3">
                        <div className="grid flex-1 gap-2 md:grid-cols-2">
                          <div className={`flex min-h-[34px] items-center rounded-sm border border-[#e4cda7] bg-[#fffaf2] px-3 text-[12px] font-bold tracking-[0.08em] text-[#8b6b4b] ${KAITI_FONT}`}>运程：{stage.label}</div>
                          <div className={`flex min-h-[34px] items-center rounded-sm border border-[#e4cda7] bg-[#fffaf2] px-3 text-[12px] font-bold tracking-[0.08em] text-[#8b6b4b] ${KAITI_FONT}`}>年龄：{stage.ageRange}</div>
                          <div className={`flex min-h-[34px] items-center rounded-sm border border-[#e4cda7] bg-[#fffaf2] px-3 text-[12px] font-bold tracking-[0.08em] text-[#8b6b4b] ${KAITI_FONT}`}>决定因素：{stageMeta.grid}</div>
                          <div className={`flex min-h-[34px] items-center rounded-sm border border-[#e4cda7] bg-[#fffaf2] px-3 text-[12px] font-bold tracking-[0.08em] text-[#8b6b4b] ${KAITI_FONT}`}>五行关系：{stage.wuxingRelation || '-'}</div>
                        </div>
                        <span className={`inline-flex h-8 w-8 shrink-0 self-center items-center justify-center rounded-full text-[11px] font-bold text-white shadow-sm sm:h-9 sm:w-9 sm:text-[12px] ${getLuckBadgeClass(stage.luck || '平')} ${KAITI_FONT}`}>
                          {(stage.luck || '平').includes('吉') && !(stage.luck || '平').includes('凶') ? '吉' : (stage.luck || '平').includes('凶') && !(stage.luck || '平').includes('吉') ? '凶' : '平'}
                        </span>
                      </div>
                      <p className="mt-3 text-[15px] leading-7 text-[#5f4738] font-sans">{stage.summary}</p>
                    </div>
                  );
                })}
              </div>

              {/* 字音字义 */}
              <SectionTitle title="字音字义" />
              <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-5">
                <div className="space-y-4 text-[15px] leading-7 text-[#5f4738] font-sans">
                  {demoData.soundMeaning.map((item, index) => {
                    // 检测是否是标题行（以数字开头或特定关键词）
                    const isTitle = /^(\d+\.|[\u4e00-\u9fa5]+[：:])/.test(item);
                    if (isTitle) {
                      const titleMatch = item.match(/^(\d+\.\s*)?([\u4e00-\u9fa5]+)[：:]?/);
                      const titleText = titleMatch ? titleMatch[2].replace(/^\d+$/, '') : item;
                      const contentText = item.replace(/^(\d+\.\s*)?[\u4e00-\u9fa5]+[：:]?\s*/, '');
                      return (
                        <div key={`sound-${index}`} className="space-y-2">
                          <div className={`text-[17px] font-bold leading-7 text-[#8f5a1b] ${KAITI_FONT}`}>{titleText}</div>
                          {contentText && <p className="text-[#5f4738]">{contentText}</p>}
                        </div>
                      );
                    }
                    return <p key={`sound-${index}`} className="pl-1">{item}</p>;
                  })}
                </div>
              </div>

              {/* 健康问题 */}
              <SectionTitle title="健康问题" />
              <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-5">
                {/* 五行分布 - 直接显示，无框 */}
                <div className="space-y-3">
                  {demoData.wuxingItems.map((item) => (
                    <div key={item.element} className="grid grid-cols-[44px,1fr,60px] items-center gap-3 text-sm text-[#5f4738]">
                      <span className="font-semibold" style={{ color: WUXING_COLOR_MAP[item.element] }}>{item.element}</span>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#efe5d7]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(8, item.percentage)}%`,
                            background: `linear-gradient(90deg, ${WUXING_COLOR_MAP[item.element]}, ${WUXING_COLOR_MAP[item.element]}cc)`,
                          }}
                        />
                      </div>
                      <span className="text-right font-semibold">{item.percentage}%</span>
                    </div>
                  ))}
                </div>
                
                {/* 五行问题说明 */}
                <div className="mt-5 space-y-3 text-[15px] leading-7 text-[#5f4738] font-sans">
                  {demoData.wuxingOverview.split('；').map((text, idx) => (
                    <p key={`wx-${idx}`}>{text.trim()}</p>
                  ))}
                </div>
                
                {/* 健康建议 */}
                <div className="mt-6 space-y-4">
                  {demoData.healthTips.map((item, index) => {
                    // 检测标题行
                    const isTitle = /^(\d+\.|[\u4e00-\u9fa5]+[：:])/.test(item);
                    if (isTitle) {
                      const titleMatch = item.match(/^(\d+\.\s*)?([\u4e00-\u9fa5]+)[：:]?/);
                      const titleText = titleMatch ? titleMatch[2].replace(/\d+/g, '') : item;
                      const contentText = item.replace(/^(\d+\.\s*)?[\u4e00-\u9fa5]+[：:]?\s*/, '');
                      return (
                        <div key={`health-${index}`} className="space-y-2">
                          <div className={`text-[17px] font-bold leading-7 text-[#8f5a1b] ${KAITI_FONT}`}>{titleText}</div>
                          {contentText && <p className="text-[15px] leading-7 text-[#5f4738] font-sans">{contentText}</p>}
                        </div>
                      );
                    }
                    return <p key={`health-${index}`} className="text-[15px] leading-7 text-[#5f4738] font-sans">{item}</p>;
                  })}
                </div>
              </div>

              {/* 姓名总评与人生建议 */}
              <SectionTitle title="姓名总评与人生建议" />
              <div className="rounded-[24px] border border-[#eadac3] bg-white/85 p-5 shadow-[0_10px_24px_rgba(131,91,51,0.06)]">
                {/* 大分数显示 */}
                <div className="mb-6 flex items-end justify-center gap-1 py-4">
                  <div className="text-[56px] font-bold leading-none text-[#C24B4B]">45</div>
                  <div className="pb-1 text-[22px] font-semibold leading-none text-[#8a6f5a]">/100</div>
                  <div className="mt-1 text-[14px] text-[#8a6f5a]">综合评分</div>
                </div>
                
                {/* heroSummary */}
                <div className="mb-5 rounded-[16px] border border-[#eadcc8] bg-[#fff9f0] px-4 py-3 text-[15px] leading-7 text-[#5f4738] font-sans">
                  {demoData.heroSummary}
                </div>

                <div className="space-y-5 text-[15px] leading-7 text-[#5f4738] font-sans">
                  <div>
                    <div className={`mb-3 text-[17px] font-bold leading-7 text-[#7a4e31] ${KAITI_FONT}`}>
                      改名建议
                    </div>
                    <div className="space-y-3">
                      {demoData.renameAdvice.map((item, index) => (
                        <p key={`rename-${index}`}>{item}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-[24px] border border-[#e6d0b2] bg-[linear-gradient(135deg,rgba(255,247,228,0.96),rgba(255,255,255,0.98))] p-5 shadow-[0_14px_34px_rgba(122,74,17,0.1)]">
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={onToggleRenameConsult}
                    className="inline-flex min-w-[220px] items-center justify-center rounded-full bg-[linear-gradient(135deg,#6f4318,#b5792b,#d7ab63)] px-8 py-3.5 text-[16px] font-semibold text-white shadow-[0_16px_28px_rgba(122,74,17,0.24)]"
                  >
                    我要改名
                  </button>
                </div>
                {showRenameConsult ? (
                  <div className="mt-5 grid gap-4 md:grid-cols-[160px,1fr] md:items-center">
                    <div className="flex justify-center">
                      <img src={PRO_TEACHER_QR_SRC} alt="若轩老师微信二维码" className="h-[148px] w-[148px] rounded-xl bg-white object-contain p-2 shadow-[0_10px_24px_rgba(122,74,17,0.12)]" />
                    </div>
                    <div className="text-[15px] leading-7 text-[#5f4738] font-sans">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-[#7f5627]">若轩老师微信号：{PRO_TEACHER_WECHAT}</p>
                        <button
                          type="button"
                          onClick={handleCopyWechat}
                          className="inline-flex items-center rounded-lg border border-[#d8b27b] bg-white px-2.5 py-1 text-xs font-semibold text-[#8b571c]"
                        >
                          复制
                        </button>
                      </div>
                      {wechatNotice ? <p className="mt-1 text-sm text-emerald-600">{wechatNotice}{isMobile && wechatNotice === '已复制成功' ? '，请到微信粘贴' : ''}</p> : null}
                      <p className="mt-2">改名是人生中的重大事件，请一定要谨慎，咨询专业老师之后再做定夺。</p>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* 按钮 */}
              <div className="mt-8 flex flex-wrap items-center gap-3" data-export-hide>
                <button
                  type="button"
                  onClick={onOpenProExport}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#2f241b] text-white shadow-[0_8px_16px_rgba(47,36,27,0.18)] transition-transform hover:-translate-y-0.5"
                  title="导出报告"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-2 rounded-full border border-[#ddccb7] bg-white px-5 py-3 text-sm font-semibold text-[#5e4638]"
                >
                  返回简版报告
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#efe4d1_0%,#f8f4ec_22%,#fbfaf7_100%)]">
      <XuanYuanTopBar
        title="轩辕国学·AI 专业解析"
        onBack={onBack}
        variant="light"
        maxWidthClassName="max-w-5xl"
      />
      <div className="mx-auto max-w-5xl px-4 py-6 pb-24">
        <div className="overflow-hidden rounded-[30px] border border-[#e8d8be] bg-[linear-gradient(180deg,rgba(255,251,244,0.98),rgba(248,241,231,0.98))] shadow-[0_24px_60px_rgba(110,76,35,0.12)]">
          <div className="relative overflow-hidden border-b border-[#ead8bd] px-6 py-10 sm:px-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,221,151,0.4),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(175,120,56,0.16),transparent_30%)]" />
            <div className="relative">
              <div className="absolute right-0 top-0 inline-flex items-center rounded-full border border-[#e3c99f] bg-white/85 px-3 py-1 text-xs font-semibold tracking-[0.22em] text-[#8f5a1b]">
                PRO
              </div>
              <h1 className={`mt-6 text-[30px] font-bold tracking-[0.08em] text-[#4d3424] sm:text-[38px] ${KAITI_FONT}`}>{result.fullName}姓名专业解析报告</h1>
              <p className="mt-3 max-w-3xl text-[15px] leading-7 text-[#60483a] font-sans">
                {reportData?.heroSummary || `${result.fullName}的专业姓名学报告正在整理中，会围绕六格数理、年龄阶段、五行分布与健康提示做更完整的现场 AI 解读。`}
              </p>
              <p className="mt-2 text-sm leading-6 text-[#856957] font-sans">
                {isDemo ? '当前为专业版姓名学报告样例页。' : '当前为实时 AI 生成的专业姓名学报告。'}
                {' '}预计生成时长约 {etaLabel}，当前分析口径：性别 {result.gender === 'female' ? '女' : '男'}，年龄 {ageText}。
              </p>
            </div>
          </div>

          <div className="px-6 py-8 sm:px-10">
            {reportError ? (
              <div className="mb-5 rounded-[20px] border border-[#ecd3b1] bg-[#fff8ee] px-4 py-3 text-[14px] leading-7 text-[#7b5d3f]">
                <p>{reportError}</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-2 inline-flex items-center rounded-full border border-[#d8b27b] bg-white px-4 py-2 text-sm font-semibold text-[#8b571c]"
                >
                  重新生成
                </button>
              </div>
            ) : null}

            <SectionTitle title="姓名档案" />
            <div className="rounded-[24px] border border-[#eadac3] bg-white/75 px-5 py-5 shadow-[0_10px_24px_rgba(131,91,51,0.06)]">
              <p className={`text-[30px] font-bold tracking-[0.22em] text-[#4a3527] ${KAITI_FONT}`}>{result.fullName}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-sm text-[#7b6758] font-sans">
                <span>性别：{result.gender === 'female' ? '女' : '男'}</span>
                <span>年龄：{ageText}</span>
                <span>三才：{result.threeTalents.sequence}</span>
              </div>
              <div className="mt-4 space-y-2 text-[15px] leading-7 text-[#5f4738] font-sans">
                {(reportData?.overview || []).map((item, index) => (
                  <p key={`overview-${index}`}>{item}</p>
                ))}
                {reportData?.currentFocus ? <p>{reportData.currentFocus}</p> : null}
              </div>
            </div>

            <SectionTitle title="六格数理基础计算" />
            <div className="p-1">
              {nameFrameNode}
            </div>

            <SectionTitle title="六格数理深度解析" />
            <div className="space-y-4">
              {(reportData?.gridAnalyses || []).map((grid) => (
                <div key={grid.key} className="rounded-[24px] border border-[#e8d8c1] bg-white/85 p-5 shadow-[0_10px_26px_rgba(122,86,45,0.06)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className={`text-[20px] font-bold text-[#4b3627] ${KAITI_FONT}`}>{grid.label} {grid.number}</h3>
                      <p className="mt-1 text-[14px] leading-7 text-[#7a5c41] font-sans">{`（${grid.number}）${grid.title}${grid.subtitle ? `：${grid.subtitle}` : ''}（${grid.genderLuck}）`}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3 text-[15px] leading-7 text-[#5d4738] font-sans">
                    <p>{grid.summary}</p>
                    {grid.base ? <p><span className="font-semibold text-[#7f5627]">基业：</span>{grid.base}</p> : null}
                    {grid.family ? <p><span className="font-semibold text-[#7f5627]">家庭：</span>{grid.family}</p> : null}
                    {grid.health ? <p><span className="font-semibold text-[#7f5627]">健康：</span>{grid.health}</p> : null}
                    {grid.meaning ? <p><span className="font-semibold text-[#7f5627]">含义：</span>{grid.meaning}</p> : null}
                  </div>
                </div>
              ))}
            </div>

            <SectionTitle title="六运联动" />
            <div className="mt-4 space-y-3">
              {(reportData?.stageAnalyses || result.stages.map((stage) => ({
                key: stage.key,
                label: stage.label,
                ageRange: stage.ageRange,
                summary: stage.summary,
              }))).map((stage) => {
                const stageMeta = result.stages.find((item) => item.key === stage.key);
                return (
                  <div key={stage.key} className="rounded-[20px] border border-[#ecdfcc] bg-[#fffdfa] px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-[#4c3727]">{stage.label}</strong>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#8a6f5a]">{stage.ageRange}</span>
                        {stageMeta ? (
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${stageStyle(stageMeta.level)}`}>
                            {stageMeta.level}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <p className="mt-2 text-[15px] leading-7 text-[#5f4738]">{stage.summary}</p>
                  </div>
                );
              })}
            </div>

            <SectionTitle title="字音字义" />
            <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-5">
              <div className="space-y-3 text-[15px] leading-7 text-[#5f4738] font-sans">
                {(reportData?.soundMeaning || []).map((item, index) => (
                  <p key={`sound-${index}`}>{item}</p>
                ))}
              </div>
            </div>

            <SectionTitle title="健康问题" />
            <div className="rounded-[24px] border border-[#ead9c3] bg-white/85 p-5">
              <div className="rounded-[18px] border border-[#eadbc7] bg-[#fff9f0] p-4">
                <div className="space-y-3">
                  {(reportData?.wuxingItems || []).map((item) => (
                    <div key={item.element} className="grid grid-cols-[44px,1fr,60px] items-center gap-3 text-sm text-[#5f4738]">
                      <span className="font-semibold" style={{ color: WUXING_COLOR_MAP[item.element] }}>{item.element}</span>
                      <div className="h-2.5 overflow-hidden rounded-full bg-[#efe5d7]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.max(8, item.percentage)}%`,
                            background: `linear-gradient(90deg, ${WUXING_COLOR_MAP[item.element]}, ${WUXING_COLOR_MAP[item.element]}cc)`,
                          }}
                        />
                      </div>
                      <span className="text-right font-semibold">{item.percentage}%</span>
                    </div>
                  ))}
                </div>
                {reportData?.wuxingOverview ? (
                  <p className="mt-4 text-[15px] leading-7 text-[#5f4738]">{reportData.wuxingOverview}</p>
                ) : null}
              </div>
              <div className="mt-5 space-y-2 text-[15px] leading-7 text-[#5f4738]">
                {(reportData?.healthTips || []).map((item, index) => (
                  <p key={`health-${index}`}>{item}</p>
                ))}
              </div>
            </div>

            <SectionTitle title="姓名总评与人生建议" />
            <div className="rounded-[24px] border border-[#eadac3] bg-white/85 p-5 shadow-[0_10px_24px_rgba(131,91,51,0.06)]">
              <div className="space-y-3 text-[15px] leading-7 text-[#5f4738]">
                {(reportData?.finalAdvice || []).map((item, index) => (
                  <p key={`final-${index}`}>{item}</p>
                ))}
                {(reportData?.renameAdvice || []).map((item, index) => (
                  <p key={`rename-${index}`}>{item}</p>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-[24px] border border-[#e6d0b2] bg-[linear-gradient(135deg,rgba(255,247,228,0.96),rgba(255,255,255,0.98))] p-5 shadow-[0_14px_34px_rgba(122,74,17,0.1)]">
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={onToggleRenameConsult}
                  className="inline-flex min-w-[220px] items-center justify-center rounded-full bg-[linear-gradient(135deg,#6f4318,#b5792b,#d7ab63)] px-8 py-3.5 text-[16px] font-semibold text-white shadow-[0_16px_28px_rgba(122,74,17,0.24)]"
                >
                  我要改名
                </button>
              </div>
              {showRenameConsult ? (
                <div className="mt-5 text-[15px] leading-7 text-[#5f4738]">
                  <p>改名是人生大事，一定要谨慎考虑。有具体问题不明白，请咨询专业老师。</p>
                  <p className="mt-3 font-semibold text-[#7f5627]">若轩老师微信：{PRO_TEACHER_WECHAT}</p>
                  <p className="mt-2">建议准备：想保留的字、避讳的字、当前最在意的事业或情感问题，以及是否有出生时辰可供辅助参考。</p>
                  <div className="mt-4 flex justify-center">
                    <img src={PRO_TEACHER_QR_SRC} alt="若轩老师微信二维码" className="h-[148px] w-[148px] rounded-xl bg-white object-contain" />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-8 flex flex-wrap gap-3" data-export-hide>
              <button
                type="button"
                onClick={onOpenProExport}
                className="inline-flex items-center gap-2 rounded-full bg-[#2f241b] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(47,36,27,0.18)] transition-transform hover:-translate-y-0.5"
              >
                导出专业报告
              </button>
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-2 rounded-full border border-[#ddccb7] bg-white px-5 py-3 text-sm font-semibold text-[#5e4638]"
              >
                返回简版报告
              </button>
            </div>

            <div className="mt-8 rounded-[20px] border border-[#ebe0ce] bg-white/80 px-5 py-4 text-[13px] leading-7 text-[#7c6a5d]">
              相信科学，理性参考。本页内容仅作姓名文化研究与娱乐参考，不替代医学、法律、财务及其他专业意见。
            </div>
            <XuanYuanReportFooter
              qrDataUrl={reportQrDataUrl}
              className="mt-8"
              textClassName="text-[#8B6B5A]"
              emphasizeClassName="text-[#7A4E31]"
              qrBorderClassName="border-[#d9ccb2]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SancaiLiugeProReportPage;
